import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { openInViewerWindow } from '../utils/viewerWindow';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Divider,
  Tooltip,
  Drawer,
  Badge,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  ChatOutlined,
  CoPresentOutlined,
  Palette as PaletteIcon,
  Launch as LaunchIcon,
  PersonAdd as PersonAddIcon,
  Translate as TranslateIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  Check as CheckIcon,
  Mic as MicIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import JiveXLogoWhite from '../statics/JiveX_RGB_white.png';
import LanguageSwitcher from './LanguageSwitcher';
import { useThemeContext } from '../theme/CustomThemeProvider';
import { apiFetch, createApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/authUtils';
import tokenService from '../utils/tokenService';
import LeaveReportDialog from './reporting/LeaveReportDialog';
import { useWebSocket } from '../contexts/WebSocketContext';
import { initFluencyDirect, generateGrammar, executeCommand as fdExecute, showVoiceTargets } from '../utils/FluencyDirectController';
import {
  initSpeechExpress,
  disconnectSpeechExpress,
  subscribeSpeechExpressStatus,
} from '../utils/SpeechExpressController';
import {
  pathToModuleId,
  moduleDefaultRoute,
  canAccessModule,
  normalizeOpenModuleIds,
  MODULE_TAB_ORDER,
} from '../utils/moduleTabs';
import { useReportingTaskCounts } from '../hooks/useReportingTaskCounts';
import { setEmbeddedMode, useEmbeddedMode } from '../utils/embeddedMode';

const MODULE_TABS_STORAGE_KEY = 'rd_module_tabs_v1';

/** Offene Befunde: bis 20 grün, ab 21 gelb, ab 50 rot */
function openFindingsCountChipColor(count) {
  if (count >= 50) return 'error';
  if (count > 20) return 'warning';
  return 'success';
}

const getMTRPopupOptions = (orientation) => {
  const screenWidth = window.screen.availWidth;
  const screenHeight = window.screen.availHeight;
  const screenLeft = window.screen.availLeft || 0;
  const screenTop = window.screen.availTop || 0;
  let width;
  let height;
  let left;
  let top;
  if (orientation === 'landscape') {
    width = screenWidth;
    height = 300;
    left = screenLeft;
    top = screenTop + screenHeight - height;
  } else {
    width = 300;
    height = screenHeight;
    left = screenLeft;
    top = screenTop;
  }
  return `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`;
};

function formatUserBarName(user) {
  if (!user) return '';
  const fn = (user.first_name || '').trim();
  const ln = (user.last_name || '').trim();
  if (ln && fn) return `${ln}, ${fn}`;
  if (fn) return fn;
  if (ln) return ln;
  return user.username || '';
}

function loadStoredOpenModuleIds() {
  try {
    const raw = localStorage.getItem(MODULE_TABS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed.openIds) && parsed.openIds.length) return parsed.openIds;
  } catch (_) {
    /* ignore */
  }
  return null;
}

function saveOpenModuleIds(ids) {
  try {
    localStorage.setItem(MODULE_TABS_STORAGE_KEY, JSON.stringify({ openIds: ids }));
  } catch (_) {
    /* ignore */
  }
}

const AppBarComponent = ({
  isLoggedIn,
  isAdmin,
  canManageReporting,
  canViewStats,
  canApprove,
  canViewCalendar,
  isMTR,
  onLogout,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, skinId, skins, setTheme } = useThemeContext();
  const { unreadCount } = useWebSocket();
  const embedded = useEmbeddedMode();
  const [approvalWorkflowEnabled, setApprovalWorkflowEnabled] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const appBarRef = useRef(null);

  // Die Kopfzeilen-Hoehe schwankt (Embedded-Modus, Reporting-Sub-Bar). Seiten
  // brauchen sie, um ihre Content-Hoehe zu berechnen, statt Pixel zu raten.
  useEffect(() => {
    const el = appBarRef.current;
    if (!el) return undefined;
    const apply = () => {
      const height = Math.round(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--rd-header-height', `${height}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isLoggedIn]);

  const gates = useMemo(
    () => ({
      isMTR,
      approvalWorkflowEnabled,
      canApprove,
      canViewCalendar,
      canViewStats,
      isAdmin,
      canManageReporting,
    }),
    [
      isMTR,
      approvalWorkflowEnabled,
      canApprove,
      canViewCalendar,
      canViewStats,
      isAdmin,
      canManageReporting,
    ],
  );

  const [openModuleIds, setOpenModuleIds] = useState(() => loadStoredOpenModuleIds() || ['reporting']);

  useEffect(() => {
    const tmr = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(tmr);
  }, []);

  useEffect(() => {
    if (!isLoggedIn || isMTR) return;
    setOpenModuleIds((prev) => {
      const normalized = normalizeOpenModuleIds(prev, gates);
      return normalized.join('|') === prev.join('|') ? prev : normalized;
    });
  }, [gates, isLoggedIn, isMTR]);

  useEffect(() => {
    if (isLoggedIn && !isMTR) {
      saveOpenModuleIds(openModuleIds);
    }
  }, [openModuleIds, isLoggedIn, isMTR]);

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const authHeaders = getAuthHeaders();
        if (!authHeaders.Authorization && !authHeaders['X-Session-ID']) {
          return;
        }
        const response = await apiFetch('/auth/me');
        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData);
        }
      } catch (error) {
        console.warn('Could not load user data for AppBar:', error);
      }
    };
    if (isLoggedIn) {
      loadCurrentUser();
    }
  }, [isLoggedIn]);

  const hasDemoPermission = () => {
    if (!currentUser) return false;
    const allowedUsers = ['visusadmin', 'visusdemo'];
    return allowedUsers.includes(currentUser.username.toLowerCase());
  };

  const hasApprovalPermission = () => canApprove === true;

  useEffect(() => {
    const loadServerConfig = async () => {
      try {
        const authHeaders = getAuthHeaders();
        if (!authHeaders.Authorization && !authHeaders['X-Session-ID']) {
          return;
        }
        const response = await apiFetch('/config/format', { headers: authHeaders });
        if (response.ok) {
          const config = await response.json();
          setApprovalWorkflowEnabled(config.use_approval_workflow === 'true');
          setEmbeddedMode(config.embedded_mode === 'true');
        } else if (response.status === 403) {
          setApprovalWorkflowEnabled(true);
        } else {
          setApprovalWorkflowEnabled(true);
        }
      } catch (error) {
        console.warn('Could not load server config for approval workflow:', error);
        setApprovalWorkflowEnabled(true);
      }
    };
    if (isLoggedIn) {
      loadServerConfig();
    }
  }, [isLoggedIn]);

  const activeModuleId = pathToModuleId(location.pathname, isMTR);

  useEffect(() => {
    if (!isLoggedIn || isMTR) return;
    const m = pathToModuleId(location.pathname, false);
    if (!m || !canAccessModule(m, gates)) return;
    setOpenModuleIds((prev) => (prev.includes(m) ? prev : [...prev, m]));
  }, [location.pathname, isLoggedIn, isMTR, gates]);

  const { myTasks, teamTasks, refresh: refreshTaskCounts } = useReportingTaskCounts(
    currentUser,
    isLoggedIn && !isMTR,
  );

  const isInReportEditor = () => location.pathname.startsWith('/report/');

  const safeNavigate = (targetPath) => {
    if (isInReportEditor()) {
      const hasUnsavedChangesFn = window.reportEditorHasUnsavedChanges;
      const hasChanges = hasUnsavedChangesFn && hasUnsavedChangesFn();
      if (hasChanges) {
        setPendingNavigation({ path: targetPath });
        setShowLeaveDialog(true);
      } else {
        clearViewerAndNavigate(targetPath);
      }
    } else {
      navigate(targetPath);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      if (window.reportEditorSaveReport) {
        await window.reportEditorSaveReport();
      }
      handleLeaveWithoutSaving();
    } catch (error) {
      console.error('Error saving report before leave:', error);
    }
  };

  const handleLeaveWithoutSaving = async () => {
    if (window.reportEditorCancelEmptyAddendum) {
      await window.reportEditorCancelEmptyAddendum();
    }
    if (window.reportEditorAllowNavigation) {
      window.reportEditorAllowNavigation();
    }
    if (pendingNavigation) {
      clearViewerAndNavigate(pendingNavigation.path);
    }
    setShowLeaveDialog(false);
    setPendingNavigation(null);
  };

  const handleCancelLeave = () => {
    setShowLeaveDialog(false);
    setPendingNavigation(null);
  };

  const clearViewerBasedOnUserPreference = async () => {
    let freshUser = currentUser;
    try {
      const response = await apiFetch('/auth/me');
      if (response.ok) {
        freshUser = await response.json();
      }
    } catch (error) {
      console.warn('Could not load fresh user data:', error);
    }
    if (!freshUser || !freshUser.viewer_preference) {
      return;
    }
    const userViewerType = freshUser.viewer_preference;
    try {
      const viewerResponse = await apiFetch('/profile/viewer-config');
      const data = viewerResponse.ok ? await viewerResponse.json() : null;
      const viewerConfig = data?.success ? data.viewer_config : null;
      const viewerSetting = viewerConfig?.[userViewerType];
      const baseUrl = viewerSetting?.base_url;
      if (!baseUrl) return;
      const clearViewerUrl = baseUrl.replace('[studyInstanceUID]', '');
      if (userViewerType === 'web') {
        if (!document.hidden) {
          openInViewerWindow(clearViewerUrl);
        }
      } else if (userViewerType === 'client') {
        const link = document.createElement('a');
        link.href = clearViewerUrl;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.warn('Error clearing viewer:', error);
    }
  };

  const clearViewerAndNavigate = async (targetPath) => {
    try {
      await clearViewerBasedOnUserPreference();
    } catch (error) {
      console.warn('Viewer clear failed:', error);
    }
    try {
      try {
        await tokenService.refreshTokenIfNeeded();
      } catch (_) {
        /* ignore */
      }
      const authResponse = await fetch(createApiUrl('auth/me'), {
        headers: getAuthHeaders(),
      });
      if (!authResponse.ok) {
        if (authResponse.status === 401) {
          alert('Ihre Sitzung ist abgelaufen. Sie werden zur Anmeldung weitergeleitet.');
        }
        onLogout();
        return;
      }
    } catch (error) {
      console.error('Auth check error:', error);
      onLogout();
      return;
    }
    setTimeout(() => navigate(targetPath), 200);
  };

  const handleProfileClick = () => {
    safeNavigate('/profile');
  };

  const closeUserMenu = () => {
    setAvatarMenuAnchor(null);
    setLanguageMenuAnchor(null);
    setThemeMenuAnchor(null);
  };

  const selectTheme = (nextSkinId, nextMode) => {
    setTheme(nextSkinId, nextMode);
    closeUserMenu();
  };

  const changeAppLanguage = (lng) => {
    i18n.changeLanguage(lng);
    closeUserMenu();
  };

  const handleLogoutClick = () => {
    closeUserMenu();
    onLogout();
  };

  const handleHomeLogoClick = () => {
    if (isMTR) {
      safeNavigate('/mtr');
    } else {
      safeNavigate('/');
    }
  };

  const openCockpitPopup = () => {
    const orientation = localStorage.getItem('mtr-orientation') || 'portrait';
    const popupOptions = getMTRPopupOptions(orientation);
    const popupWindow = window.open('/radiographer-cockpit', 'mtr-dashboard', popupOptions);
    if (popupWindow) {
      popupWindow.focus();
    }
  };

  const openChatGlobal = () => {
    window.dispatchEvent(new Event('openChat'));
  };

  const openModuleFromDrawer = (moduleId) => {
    if (!canAccessModule(moduleId, gates)) return;
    setOpenModuleIds((prev) => (prev.includes(moduleId) ? prev : [...prev, moduleId]));
    safeNavigate(moduleDefaultRoute(moduleId));
    setDrawerOpen(false);
  };

  const handleModuleTabClick = (moduleId) => {
    safeNavigate(moduleDefaultRoute(moduleId));
  };

  const handleCloseModuleTab = (e, moduleId) => {
    e.stopPropagation();
    if (openModuleIds.length <= 1) return;
    const idx = openModuleIds.indexOf(moduleId);
    const nextOpen = openModuleIds.filter((id) => id !== moduleId);
    setOpenModuleIds(nextOpen);
    if (activeModuleId === moduleId) {
      const fallback = nextOpen[Math.max(0, idx - 1)] || nextOpen[0];
      safeNavigate(moduleDefaultRoute(fallback));
    }
  };

  const orderedOpenTabs = useMemo(() => {
    const openSet = new Set(openModuleIds);
    return MODULE_TAB_ORDER.filter((id) => openSet.has(id) && canAccessModule(id, gates));
  }, [openModuleIds, gates]);

  const dateTimeLabel = useMemo(() => {
    const loc = (i18n.language || 'de').replace('_', '-');
    try {
      return new Intl.DateTimeFormat(loc, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(now);
    } catch (_) {
      return now.toLocaleString();
    }
  }, [now]);

  const [speechExpressStatus, setSpeechExpressStatus] = useState('disconnected');

  useEffect(() => {
    if (!isLoggedIn) {
      return undefined;
    }
    try {
      initFluencyDirect();
      window._fdControllerReady = true;
      window._fdGetGrammarImpl = generateGrammar;
      window._fdExecuteCommandImpl = fdExecute;
      window._showVoiceTargetsImpl = showVoiceTargets;
    } catch (error) {
      console.error('[FluencyDirect] Init error:', error);
    }
    try {
      initSpeechExpress();
    } catch (error) {
      console.error('[SpeechExpress] Init error:', error);
    }
    const unsubscribeStatus = subscribeSpeechExpressStatus(setSpeechExpressStatus);
    return () => {
      window._fdControllerReady = false;
      unsubscribeStatus();
      disconnectSpeechExpress();
    };
  }, [isLoggedIn]);

  const speechExpressStatusLabel = useMemo(() => {
    switch (speechExpressStatus) {
      case 'recording':
        return t('voiceControl.speechExpress.recording');
      case 'target_claimed':
        return t('voiceControl.speechExpress.targetClaimed');
      case 'connected':
        return t('voiceControl.speechExpress.connected');
      case 'reconnecting':
        return t('voiceControl.speechExpress.reconnecting');
      default:
        return t('voiceControl.speechExpress.disconnected');
    }
  }, [speechExpressStatus, t]);

  const speechExpressStatusColor = useMemo(() => {
    switch (speechExpressStatus) {
      case 'recording':
      case 'target_claimed':
        return 'var(--rd-success)';
      case 'connected':
      case 'reconnecting':
        return 'var(--rd-warning)';
      default:
        return 'rgba(255,255,255,0.45)';
    }
  }, [speechExpressStatus]);

  useEffect(() => {
    if (!isLoggedIn || !window._fdControllerReady) return;
    if (typeof window.reloadGrammar === 'function') {
      window.reloadGrammar();
    }
  }, [isLoggedIn, i18n.language]);

  useEffect(() => {
    if (!isLoggedIn || !window._fdControllerReady) return;
    const timer = setTimeout(() => {
      if (typeof window.reloadGrammar === 'function') {
        window.reloadGrammar();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isLoggedIn, location.pathname, openModuleIds, isMTR]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const handleVoiceNavigate = (event) => {
      const { route } = event.detail;
      if (route && route !== location.pathname) {
        safeNavigate(route);
      }
    };
    window.addEventListener('voiceNavigate', handleVoiceNavigate);
    return () => window.removeEventListener('voiceNavigate', handleVoiceNavigate);
  }, [isLoggedIn, location.pathname]);

  // Chrome tokens, not `primary`: the strip is the largest surface in the app
  // and a skin has to be able to keep it neutral.
  const navStripBg = 'var(--rd-chrome-bg)';
  const navStripFg = 'var(--rd-chrome-fg)';
  const inactiveTabBg = 'var(--rd-chrome-tab-inactive)';
  const inactiveTabHoverBg = 'var(--rd-chrome-tab-inactive-hover)';
  const activeTabBg = 'var(--rd-bg-paper)';
  const activeTabHoverBg = 'var(--rd-bg-paper)';

  const moduleVoiceId = (id) => {
    if (id === 'reporting') return 'reportingModuleTab';
    if (id === 'approval') return 'approvalButton';
    if (id === 'calendar') return 'calendarButton';
    if (id === 'search') return 'searchButton';
    if (id === 'statistics') return 'statisticsButton';
    if (id === 'admin') return 'adminButton';
    return `moduleTab_${id}`;
  };

  const moduleLabel = (id) => {
    switch (id) {
      case 'reporting':
        return t('navigation.modules.reporting');
      case 'approval':
        return t('navigation.modules.approval');
      case 'calendar':
        return t('navigation.modules.calendar');
      case 'search':
        return t('navigation.modules.search');
      case 'statistics':
        return t('navigation.modules.statistics');
      case 'admin':
        return t('navigation.modules.admin');
      default:
        return id;
    }
  };

  const renderLoggedOutBar = () => (
    <AppBar ref={appBarRef} position="static" className="app-bar" color="default" elevation={1}>
      <Toolbar
        sx={{
          width: '100%',
          justifyContent: 'flex-end',
          minHeight: 48,
          '&.MuiToolbar-gutters': { px: 2 },
        }}
      >
        <LanguageSwitcher />
      </Toolbar>
    </AppBar>
  );

  const renderMenuButton = ({ compact = false } = {}) => (
    <IconButton
      id="menuButton"
      edge={compact ? false : 'start'}
      color="inherit"
      aria-label={t('navigation.menu')}
      onClick={() => setDrawerOpen(true)}
      size={compact ? 'small' : 'medium'}
    >
      <MenuIcon sx={{ fontSize: compact ? 20 : 24 }} />
    </IconButton>
  );

  const renderMicIndicator = ({ compact = false } = {}) => (
    <Tooltip title={speechExpressStatusLabel}>
      <MicIcon
        aria-label={speechExpressStatusLabel}
        sx={{
          fontSize: compact ? 20 : 22,
          color: speechExpressStatusColor,
          opacity: speechExpressStatus === 'disconnected' ? 0.7 : 1,
          animation: speechExpressStatus === 'recording' ? 'pulse 1.2s ease-in-out infinite' : 'none',
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.45 },
            '100%': { opacity: 1 },
          },
        }}
      />
    </Tooltip>
  );

  const renderChatButton = ({ compact = false } = {}) => (
    <Tooltip
      title={
        unreadCount > 0
          ? `${unreadCount} ${t('chat.unreadMessages', 'ungelesene Nachrichten')}`
          : t('navigation.chat', 'Chat')
      }
    >
      <IconButton
        id="chatButton"
        color="inherit"
        onClick={openChatGlobal}
        size={compact ? 'small' : 'medium'}
      >
        <Badge
          badgeContent={unreadCount > 0 ? unreadCount : 0}
          color="error"
          max={99}
          invisible={!unreadCount}
        >
          <ChatOutlined sx={{ fontSize: compact ? 20 : 24 }} />
        </Badge>
      </IconButton>
    </Tooltip>
  );

  const renderAvatarTrigger = ({ compact = false } = {}) => (
    <Box
      id="appBarUserButton"
      title={compact ? formatUserBarName(currentUser) || '' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        setLanguageMenuAnchor(null);
        setAvatarMenuAnchor(e.currentTarget);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 0 : 1,
        pl: 0.5,
        pr: compact ? 0.5 : 1,
        cursor: 'pointer',
        borderRadius: 1,
        '&:hover': { bgcolor: 'color-mix(in srgb, var(--rd-chrome-fg) 18%, transparent)' },
      }}
    >
      <Avatar
        sx={{
          width: compact ? 26 : 36,
          height: compact ? 26 : 36,
          bgcolor: 'var(--rd-chrome-surface)',
          color: navStripFg,
          fontSize: compact ? '0.75rem' : '0.95rem',
        }}
      >
        {(formatUserBarName(currentUser) || '?').charAt(0).toUpperCase()}
      </Avatar>
      {!compact && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {formatUserBarName(currentUser) || '…'}
        </Typography>
      )}
    </Box>
  );

  const renderPrimaryToolbar = () => (
    <Toolbar
      sx={{
        width: '100%',
        minHeight: 56,
        px: 1,
        gap: 1,
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: navStripBg,
        color: navStripFg,
        '&.MuiToolbar-gutters': { px: 1 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.5, minWidth: 0 }}>
        {renderMenuButton()}
        <Box
          id="homeButton"
          component="img"
          src={JiveXLogoWhite}
          alt="JiveX"
          onClick={handleHomeLogoClick}
          sx={{ height: 40, cursor: 'pointer' }}
        />
        <Typography
          id="dashboardButton"
          variant="subtitle1"
          component="div"
          onClick={handleHomeLogoClick}
          sx={{
            cursor: 'pointer',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {isMTR ? t('navigation.mtr.barTitle') : t('navigation.appBarTitle')}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          textAlign: 'center',
          px: 1,
          minWidth: 0,
          fontWeight: 500,
          letterSpacing: 0.2,
          userSelect: 'none',
        }}
      >
        {dateTimeLabel}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {renderMicIndicator()}
        {renderChatButton()}
        {renderAvatarTrigger()}
      </Box>
    </Toolbar>
  );

  const renderModuleTabBar = () => {
    // Im Embedded-Modus ist diese Leiste die einzige Kopfzeile und muss daher
    // auch fuer MTR gerendert werden (nur ohne Modul-Tabs).
    if (isMTR && !embedded) return null;
    return (
      <Box
        sx={{
          width: '100%',
          bgcolor: navStripBg,
          color: navStripFg,
          borderBottom: '1px solid',
          borderColor: 'var(--rd-chrome-tab-inactive)',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: embedded ? 'flex-end' : 'stretch',
            gap: 0,
            p: 0,
            m: 0,
            px: embedded ? 0.5 : 0,
            minHeight: embedded ? 36 : 32,
            overflowX: 'hidden',
            flexWrap: 'nowrap',
          }}
        >
        {embedded && (
          <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'center', flexShrink: 0, mr: 0.5 }}>
            {renderMenuButton({ compact: true })}
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: embedded ? 'flex-end' : 'stretch',
            flex: 1,
            minWidth: 0,
            gap: 0,
            overflowX: 'hidden',
            flexWrap: 'nowrap',
          }}
        >
        {(isMTR ? [] : orderedOpenTabs).map((id) => {
          const active = activeModuleId === id;
          return (
            <Box
              key={id}
              id={moduleVoiceId(id)}
              onClick={() => handleModuleTabClick(id)}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flex: '1 1 0px',
                maxWidth: 400,
                minWidth: 80,
                height: 32,
                minHeight: 32,
                maxHeight: 32,
                m: 0,
                px: 2,
                py: 0,
                boxSizing: 'border-box',
                cursor: 'pointer',
                userSelect: 'none',
                borderRadius: '8px 8px 0 0',
                bgcolor: active ? activeTabBg : inactiveTabBg,
                color: active ? 'text.primary' : navStripFg,
                fontSize: '0.8125rem',
                fontWeight: active ? 700 : 500,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: active ? activeTabHoverBg : inactiveTabHoverBg,
                },
              }}
            >
              <Typography component="span" noWrap variant="body2" sx={{ fontSize: 'inherit', fontWeight: 'inherit', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {moduleLabel(id)}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => handleCloseModuleTab(e, id)}
                sx={{
                  m: 0,
                  p: '2px',
                  color: active ? 'text.secondary' : navStripFg,
                }}
                aria-label={t('navigation.closeTab', 'Schließen')}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          );
        })}
        </Box>
        {embedded && (
          <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'center', flexShrink: 0, gap: 0.25, ml: 0.5 }}>
            {renderMicIndicator({ compact: true })}
            {renderChatButton({ compact: true })}
            {renderAvatarTrigger({ compact: true })}
          </Box>
        )}
        </Box>
      </Box>
    );
  };

  const renderReportingSubBar = () => {
    if (isMTR || activeModuleId !== 'reporting') return null;
    const path = location.pathname;
    const inReportEditor = path.startsWith('/report/');
    const dashActive = path === '/' && !inReportEditor;
    const myActive = path.startsWith('/my-tasks') || inReportEditor;
    const teamActive = path.startsWith('/team-tasks');
    const subBtn = (label, isActive, route, voiceId, badge) => (
      <Box
        key={voiceId}
        id={voiceId}
        onClick={() => safeNavigate(route)}
        sx={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 2.5,
          m: 0,
          height: 50,
          minHeight: 50,
          maxHeight: 50,
          boxSizing: 'border-box',
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: '8px 8px 0 0',
          bgcolor: isActive ? activeTabBg : inactiveTabBg,
          color: isActive ? 'text.primary' : navStripFg,
          fontWeight: isActive ? 700 : 500,
          boxShadow: 'none',
          '&:hover': {
            bgcolor: isActive ? activeTabHoverBg : inactiveTabHoverBg,
          },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>
          {label}
        </Typography>
        {badge}
      </Box>
    );
    const chipSx = {
      height: 22,
      minWidth: 22,
      '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem', fontWeight: 700 },
    };
    const myBadge =
      myTasks > 0 ? (
        <Chip
          size="small"
          color={openFindingsCountChipColor(myTasks)}
          label={myTasks > 99 ? '99+' : String(myTasks)}
          sx={chipSx}
        />
      ) : null;
    const teamBadge =
      teamTasks > 0 ? (
        <Chip
          size="small"
          color={openFindingsCountChipColor(teamTasks)}
          label={teamTasks > 99 ? '99+' : String(teamTasks)}
          sx={chipSx}
        />
      ) : null;
    return (
      <Box
        sx={{
          width: '100%',
          mt: '5px',
          boxSizing: 'border-box',
          display: 'flex',
          justifyContent: 'flex-start',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 0,
            p: 0,
            m: 0,
            width: 'fit-content',
            maxWidth: '100%',
            minHeight: 50,
            overflowX: 'auto',
            flexWrap: 'nowrap',
            boxSizing: 'border-box',
          }}
        >
          {subBtn(t('navigation.subTabs.dashboard'), dashActive, '/', 'dashboardButton', null)}
          {subBtn(t('navigation.subTabs.myTasks'), myActive, '/my-tasks', 'myTasksButton', myBadge)}
          {subBtn(t('navigation.subTabs.teamTasks'), teamActive, '/team-tasks', 'teamTasksButton', teamBadge)}
        </Box>
      </Box>
    );
  };

  const renderReaderDrawerModules = () => {
    const openedIds = orderedOpenTabs;
    const availableIds = MODULE_TAB_ORDER.filter(
      (id) => canAccessModule(id, gates) && !openModuleIds.includes(id),
    );

    const moduleRow = (id, onActivate) => (
      <Box
        key={id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover .drawer-label': { color: 'var(--rd-primary)' },
        }}
        onClick={() => {
          onActivate(id);
        }}
      >
        <Typography className="drawer-label" variant="body2">
          {moduleLabel(id)}
        </Typography>
      </Box>
    );

    return (
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', letterSpacing: 0.5, mb: 0.5 }}>
          {t('navigation.drawerOpenedModules')}
        </Typography>
        {openedIds.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 0.5, pl: 0.5 }}>
            {t('navigation.drawerNoOpenedModules')}
          </Typography>
        ) : (
          openedIds.map((id) =>
            moduleRow(id, () => {
              handleModuleTabClick(id);
              setDrawerOpen(false);
            }),
          )
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', letterSpacing: 0.5, mb: 0.5 }}>
          {t('navigation.drawerAvailableModules')}
        </Typography>
        {availableIds.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 0.5, pl: 0.5 }}>
            {t('navigation.drawerNoAvailableModules')}
          </Typography>
        ) : (
          availableIds.map((id) => moduleRow(id, openModuleFromDrawer))
        )}
      </Box>
    );
  };

  const renderMtrDrawerEntries = () => (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover .drawer-label': { color: 'var(--rd-primary)' },
        }}
        onClick={() => {
          safeNavigate('/mtr');
          setDrawerOpen(false);
        }}
      >
        <Typography className="drawer-label" variant="body2">
          {t('navigation.mtr.dashboard', 'Dashboard')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover .drawer-label': { color: 'var(--rd-primary)' },
        }}
        onClick={() => {
          openCockpitPopup();
          setDrawerOpen(false);
        }}
      >
        <LaunchIcon className="drawer-icon" fontSize="small" />
        <Typography className="drawer-label" variant="body2">
          {t('navigation.openCockpit')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover .drawer-label': { color: 'var(--rd-primary)' },
        }}
        onClick={() => {
          safeNavigate('/add-patient');
          setDrawerOpen(false);
        }}
      >
        <PersonAddIcon className="drawer-icon" fontSize="small" />
        <Typography className="drawer-label" variant="body2">
          {t('navigation.mtr.addPatient', 'Patient anlegen')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover .drawer-label': { color: 'var(--rd-primary)' },
        }}
        onClick={() => {
          safeNavigate('/search');
          setDrawerOpen(false);
        }}
      >
        <Typography className="drawer-label" variant="body2">
          {t('navigation.search', 'Suche')}
        </Typography>
      </Box>
    </>
  );

  const langIs = (code) => (i18n.language || 'de').toLowerCase().startsWith(code);

  // One entry per skin and mode, so new skins show up without touching the menu.
  const themeOptions = skins.flatMap((skin) => [
    { skinId: skin.id, mode: 'light', label: `${skin.label} – ${t('theme.light', 'Hell')}` },
    { skinId: skin.id, mode: 'dark', label: `${skin.label} – ${t('theme.dark', 'Dunkel')}` },
  ]);

  const renderAvatarUserMenus = () => (
    <>
      <Menu
        anchorEl={avatarMenuAnchor}
        open={Boolean(avatarMenuAnchor)}
        onClose={closeUserMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
      >
        <MenuItem
          id="profileButton"
          onClick={() => {
            handleProfileClick();
            closeUserMenu();
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('navigation.profile', 'Profil')}</ListItemText>
        </MenuItem>
        <MenuItem
          id="themeMenuButton"
          onClick={(e) => {
            e.stopPropagation();
            setLanguageMenuAnchor(null);
            setThemeMenuAnchor(themeMenuAnchor ? null : e.currentTarget);
          }}
          sx={{ pr: 1 }}
        >
          <ListItemIcon>
            <PaletteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ flex: 1 }}>{t('navigation.theme', 'Theme')}</ListItemText>
          <ChevronRightIcon sx={{ fontSize: 18, opacity: 0.7 }} />
        </MenuItem>
        <MenuItem
          id="languageMenuButton"
          onClick={(e) => {
            e.stopPropagation();
            setThemeMenuAnchor(null);
            setLanguageMenuAnchor(languageMenuAnchor ? null : e.currentTarget);
          }}
          sx={{ pr: 1 }}
        >
          <ListItemIcon>
            <TranslateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ flex: 1 }}>{t('navigation.language', 'Sprache')}</ListItemText>
          <ChevronRightIcon sx={{ fontSize: 18, opacity: 0.7 }} />
        </MenuItem>
        {hasDemoPermission() && (
          <MenuItem
            onClick={() => {
              safeNavigate('/demo');
              closeUserMenu();
            }}
          >
            <ListItemIcon>
              <CoPresentOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('navigation.demo')}</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            handleLogoutClick();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('navigation.logout')}</ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={() => setLanguageMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
      >
        <MenuItem onClick={() => changeAppLanguage('de')}>
          <ListItemIcon>{langIs('de') ? <CheckIcon fontSize="small" /> : <Box component="span" sx={{ width: 24, display: 'inline-block' }} />}</ListItemIcon>
          <ListItemText>Deutsch</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => changeAppLanguage('en')}>
          <ListItemIcon>{langIs('en') ? <CheckIcon fontSize="small" /> : <Box component="span" sx={{ width: 24, display: 'inline-block' }} />}</ListItemIcon>
          <ListItemText>English</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => changeAppLanguage('fr')}>
          <ListItemIcon>{langIs('fr') ? <CheckIcon fontSize="small" /> : <Box component="span" sx={{ width: 24, display: 'inline-block' }} />}</ListItemIcon>
          <ListItemText>Français</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => changeAppLanguage('nl')}>
          <ListItemIcon>{langIs('nl') ? <CheckIcon fontSize="small" /> : <Box component="span" sx={{ width: 24, display: 'inline-block' }} />}</ListItemIcon>
          <ListItemText>Nederlands</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => changeAppLanguage('id')}>
          <ListItemIcon>{langIs('id') ? <CheckIcon fontSize="small" /> : <Box component="span" sx={{ width: 24, display: 'inline-block' }} />}</ListItemIcon>
          <ListItemText>Bahasa Indonesia</ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={themeMenuAnchor}
        open={Boolean(themeMenuAnchor)}
        onClose={() => setThemeMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 190 } } }}
      >
        {themeOptions.map((option) => (
          <MenuItem
            key={`${option.skinId}-${option.mode}`}
            onClick={() => selectTheme(option.skinId, option.mode)}
          >
            <ListItemIcon>
              {option.skinId === skinId && option.mode === mode
                ? <CheckIcon fontSize="small" />
                : <Box component="span" sx={{ width: 24, display: 'inline-block' }} />}
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  if (!isLoggedIn) {
    return renderLoggedOutBar();
  }

  return (
    <>
      <AppBar
        ref={appBarRef}
        position="static"
        className="app-bar"
        elevation={0}
        sx={{ bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' }}
      >
        {!embedded && renderPrimaryToolbar()}
        {renderModuleTabBar()}
        {renderReportingSubBar()}
      </AppBar>
      {renderAvatarUserMenus()}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280, p: 2 }}>
          {isMTR ? renderMtrDrawerEntries() : renderReaderDrawerModules()}
        </Box>
      </Drawer>
      <LeaveReportDialog
        open={showLeaveDialog}
        onClose={handleCancelLeave}
        onSaveAndLeave={handleSaveAndLeave}
        onLeaveWithoutSaving={handleLeaveWithoutSaving}
      />
    </>
  );
};

export default AppBarComponent;
